import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useYoutubeBrandAccountsQuery } from '@anytag/graphql';
import { NoDataContentWrapperProps, TextCutter } from '@anytag/shared-atoms';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { InfluencerPostDetailsModal } from '@anytag/shared-molecules';
import { formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { useInfluencerProfileOpacityContent } from '../../hooks';
import { InfluencerSelectedAccountInfo } from '../../types';
import { InfluencerProfileMentionedAccountsWrapper } from '../shared';

const BRAND_POST_PARAMS_KEY = 'brandPostId';

export interface InfluencerYoutubeProfileMentionedAccountsProps
  extends Pick<NoDataContentWrapperProps, 'wrapperText' | 'wrapperLink'> {
  className?: string;
  influencerId: number;
  socialAccount: InfluencerSelectedAccountInfo;
}

export const InfluencerYoutubeProfileMentionedAccounts = ({
  wrapperLink,
  wrapperText,
  influencerId,
  socialAccount
}: InfluencerYoutubeProfileMentionedAccountsProps) => {
  const { t } = useTranslation();
  const [selectedPostIds, setSelectedPostIds] = useState<readonly number[]>([]);
  const { setPostDetailsParam } = usePostDetailsParam(BRAND_POST_PARAMS_KEY);
  const { opacityContent } = useInfluencerProfileOpacityContent({
    influencerId,
    socialAccount,
    skipEstimateContent: true
  });
  const { data, loading } = useYoutubeBrandAccountsQuery({
    skip: !influencerId,
    variables: { influencerId, youtubeAccountSystemId: socialAccount.id }
  });

  const brandAccountsData = data?.youtubeBrandAccounts;

  return (
    <>
      <InfluencerProfileMentionedAccountsWrapper
        {...opacityContent}
        loading={loading}
        wrapperText={wrapperText || opacityContent.wrapperText}
        wrapperLink={wrapperLink || opacityContent.wrapperLink}
        data={brandAccountsData?.brandAccounts || []}
        headerMetrics={[
          {
            label: t<string>('HeaderColumn.Avg Engagement Rate'),
            value: formatPercent(brandAccountsData?.averageEngagementRate)
          },
          {
            label: t<string>('Avg Views'),
            value: formatIntNumber(brandAccountsData?.averageViews)
          }
        ]}
        renderItem={({ styles, account: { name, postIds, mentionPostsCount, averageViews } }) => (
          <div css={styles.brandAccountItem}>
            <TextCutter text={name} lines={1} css={styles.brandAccountName} />
            <p
              css={styles.brandAccountMention}
              onClick={() => {
                if (postIds.length) {
                  setSelectedPostIds(postIds);
                  setPostDetailsParam(postIds[0]);
                }
              }}
            >
              {t('mention', { count: mentionPostsCount })}
            </p>
            <TextCutter
              lines={1}
              css={styles.brandAccountRate}
              text={`${t(`Avg View`)}: ${formatIntNumber(averageViews)}`}
            />
          </div>
        )}
      />

      <InfluencerPostDetailsModal
        paramKey={BRAND_POST_PARAMS_KEY}
        posts={selectedPostIds.map((postId) => ({ id: postId, influencerId }))}
      />
    </>
  );
};
